import { Link } from 'react-router-dom';
import GuideLayout from '../../components/guides/GuideLayout';
import GuideCTA from '../../components/guides/GuideCTA';
import { GuideSection, GuideFaqItem, GuideChecklist, GuideNote, SourceLink } from '../../components/guides/GuideParts';

const SLUG = 'how-to-request-your-medical-records';

export const RequestMedicalRecords = () => (
  <GuideLayout slug={SLUG}>
    <GuideSection>
      <p>
        GP surgeries and hospitals hold detailed health records, and for anyone who moved
        between placements as a child, those records are often scattered across several
        practices — a new GP every time you moved, and possibly several hospitals for one-off
        appointments or admissions. You have the same right of access here as anywhere else,
        under Article 15 of the UK GDPR.
      </p>
    </GuideSection>

    <GuideSection eyebrow="Who to ask" title="GP, hospital, or both">
      <GuideChecklist
        items={[
          "Your current GP surgery holds your main record, but it usually only goes back as far as when you registered — earlier records normally transfer with you, but this doesn't always happen cleanly, especially across several childhood moves.",
          "A GP surgery you're no longer registered with may still hold historic records, or may have passed them on when you left. If you're not sure, your current GP or NHS England can help trace where an old record went.",
          'A hospital keeps its own separate record for any appointment, admission or treatment — request from the specific hospital trust, not the GP, for anything that happened there.',
          "If you were looked after, some health information may also sit in your local authority's records as a looked-after child health assessment — this is a separate request to the council, not the NHS."
        ]}
      />
    </GuideSection>

    <GuideSection eyebrow="What to ask for" title="Being specific helps them search">
      <GuideChecklist
        items={[
          'Your full GP record, including consultation notes, referral letters and any summary care record.',
          'Immunisation and health surveillance history.',
          "Copies of any letters between your GP and a hospital, specialist, or CAMHS (Child and Adolescent Mental Health Services).",
          'Hospital records for any specific admission or appointment you remember, including discharge summaries.',
          'Looked-after child health assessments, if applicable, from the local authority.',
        ]}
      />
      <p>
        If you were registered under a different name at any point — a foster carer&apos;s surname,
        for example — mention it. NHS records are searched by the name and details held at the
        time, and a name change can otherwise split your history across records that don&apos;t
        obviously link up.
      </p>
    </GuideSection>

    <GuideNote>
      <p>
        <strong>If you just want current access, not the full history:</strong> the NHS App
        gives many patients online access to parts of their current GP record, such as
        medications and test results, without needing a formal SAR at all. It is not a
        substitute for a full historic record, but worth knowing about if that is all you need.
      </p>
    </GuideNote>

    <GuideCTA />

    <GuideSection eyebrow="What happens next" title="Deadlines and what to expect">
      <p>
        A GP surgery or hospital trust has <strong className="text-ink">one calendar month</strong>{' '}
        to respond, extendable by up to two further months for a complex request. Health records
        can include information about other people mentioned alongside you — a parent&apos;s health
        history noted in your own file, for instance — which may be redacted, but the reasoning
        behind your own diagnoses, treatment and care should not be.
      </p>
    </GuideSection>

    <GuideSection eyebrow="Common questions" title="Questions people ask us most" tinted>
      <div className="space-y-2" data-testid="medical-records-faq">
        <GuideFaqItem
          q="I don't know which GP surgeries I was registered with as a child."
          a="Your current GP or NHS England's Primary Care Support England service can often trace your NHS number and registration history even if you can't remember the surgeries yourself. Give them what you do remember — an area, an approximate age — and they can help narrow it down."
        />
        <GuideFaqItem
          q="Can I get records about a parent or sibling's health that might explain my own care history?"
          a="Only your own personal data — someone else's health record is their own personal data, not yours, even if it's relevant background to your own story. If their health was a documented reason for decisions made about your care, that reasoning about your situation should appear in your own social care or health records instead."
        />
        <GuideFaqItem
          q="What about mental health services like CAMHS?"
          a="CAMHS and other mental health services are usually run by an NHS trust and hold their own records, separate from your GP — request from the specific trust or service you attended, and they're subject to the same one-month deadline as any other health record."
        />
        <GuideFaqItem
          q="Is there a fee for medical records?"
          a="No, not for a standard request. The same rule applies as any SAR: free, unless the request is manifestly unfounded or excessive, or you're asking for further copies of something you've already been given."
        />
      </div>
    </GuideSection>

    <GuideSection eyebrow="Read more" title="Where this comes from">
      <p>
        NHS England explains the practical routes for requesting records in{' '}
        <SourceLink href="https://www.england.nhs.uk/contact-us/common-questions/how-do-i-get-a-copy-of-my-health-medical-records/">
          Getting copies of medical records
        </SourceLink>
        . The legal right itself is the same Article 15 UK GDPR right covered in the ICO&apos;s{' '}
        <SourceLink href="https://ico.org.uk/for-organisations/uk-gdpr-guidance-and-resources/individual-rights/right-of-access/">
          right of access guidance
        </SourceLink>
        — see our{' '}
        <Link to="/guides/what-is-a-subject-access-request" className="text-brand underline hover:opacity-90">
          plain-English guide to SARs
        </Link>{' '}
        for the general mechanics.
      </p>
    </GuideSection>

    <GuideCTA
      heading="Add every GP and hospital you remember"
      body="We'll draft a separate request for each one, send it, and track the deadline — so you don't have to keep a spreadsheet of who you've written to."
      testIdPrefix="guide-cta-end"
    />
  </GuideLayout>
);

export default RequestMedicalRecords;
